import React, { useMemo, useState } from "react";
import {
  PhotoIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSliders,
  useToggleSlider,
  useDeleteSlider,
  useUpdateSlider,
} from "../hooks/useSliders";
import { Slider } from "../services/api";
import SliderFormModal from "../components/sliders/SliderFormModal";
import SortableSlideRow from "../components/sliders/SortableSlideRow";

const Sliders: React.FC = () => {
  const { data: sliders, isLoading } = useSliders();
  const toggleSlider = useToggleSlider();
  const deleteSlider = useDeleteSlider();
  const updateSlider = useUpdateSlider();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSlider, setSelectedSlider] = useState<Slider | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sliderToDelete, setSliderToDelete] = useState<Slider | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [sliderDetails, setSliderDetails] = useState<Slider | null>(null);
  const [localSliders, setLocalSliders] = useState<Slider[]>([]);

  // Sort a copy so the react-query cache array is never mutated in place
  const sortedSliders = useMemo(
    () =>
      sliders
        ? [...sliders].sort((a, b) => a.displayOrder - b.displayOrder)
        : [],
    [sliders],
  );

  // Update local sliders when data changes
  React.useEffect(() => {
    if (sliders) {
      setLocalSliders(sortedSliders);
    }
  }, [sliders, sortedSliders]);

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Helper to get full image URL
  const getImageUrl = (url: string) => {
    if (!url) return "";
    // If already a full URL, return as is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    // If relative path, prepend BPE-BE base URL
    const bpeBeUrl =
      process.env.REACT_APP_BPE_BE_URL || "http://localhost:5000";
    return `${bpeBeUrl}${url}`;
  };

  const handleCreate = () => {
    setSelectedSlider(null);
    setIsFormOpen(true);
  };

  const handleEdit = (slider: Slider, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedSlider(slider);
    setIsFormOpen(true);
  };

  const handleRowClick = (slider: Slider) => {
    setSliderDetails(slider);
    setDetailsModalOpen(true);
  };

  const handleToggle = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    toggleSlider.mutate(id);
  };

  const handleDeleteClick = (slider: Slider, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSliderToDelete(slider);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (sliderToDelete) {
      deleteSlider.mutate(sliderToDelete._id);
      setDeleteModalOpen(false);
      setSliderToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSliderToDelete(null);
  };

  const getCountryFlags = (countries: string[]) => {
    return (
      countries.slice(0, 3).join(", ") +
      (countries.length > 3 ? ` +${countries.length - 3}` : "")
    );
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localSliders.findIndex((s) => s._id === active.id);
    const newIndex = localSliders.findIndex((s) => s._id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for smooth UX
    const newSliders = arrayMove(localSliders, oldIndex, newIndex);

    // Update displayOrder for all slides starting from 0
    const updatedSliders = newSliders.map((slide, index) => ({
      ...slide,
      displayOrder: index,
    }));

    setLocalSliders(updatedSliders);

    // Update displayOrder in backend for all slides (to fix conflicts)
    const promises = updatedSliders.map((slide, index) => {
      return updateSlider.mutateAsync({
        id: slide._id,
        data: { displayOrder: index },
      });
    });

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Failed to update slide order:");
      // Revert on error
      setLocalSliders(sortedSliders);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Homepage Slides
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage mobile app homepage slider slides
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Slide
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Slides
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {sliders?.length || 0}
              </p>
            </div>
            <PhotoIcon className="h-10 w-10 text-gray-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {sliders?.filter((s) => s.isActive).length || 0}
              </p>
            </div>
            <EyeIcon className="h-10 w-10 text-green-400" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Inactive
              </p>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {sliders?.filter((s) => !s.isActive).length || 0}
              </p>
            </div>
            <EyeSlashIcon className="h-10 w-10 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Sliders List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-24 bg-gray-200 dark:bg-gray-700 rounded"
                />
              ))}
            </div>
          </div>
        ) : localSliders.length > 0 ? (
          <div className="overflow-x-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {/* Drag Handle */}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Preview
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Language
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Countries
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <SortableContext
                    items={localSliders.map((s) => s._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localSliders.map((slide) => (
                      <SortableSlideRow
                        key={slide._id}
                        slide={slide}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        onToggle={handleToggle}
                        onRowClick={handleRowClick}
                        getImageUrl={getImageUrl}
                        getCountryFlags={getCountryFlags}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </div>
        ) : (
          <div className="p-12 text-center">
            <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No slides yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first homepage slide to get started
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Slider
            </button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <SliderFormModal
        slider={selectedSlider}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedSlider(null);
        }}
      />

      {/* Details Modal */}
      {detailsModalOpen && sliderDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => setDetailsModalOpen(false)}
            />

            {/* Modal */}
            <div className="inline-block w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Slider Details
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Created:{" "}
                    {new Date(sliderDetails.createdAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setDetailsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                {/* Image Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image Preview
                  </label>
                  <div className="relative">
                    <img
                      src={getImageUrl(
                        sliderDetails.imageUrlCropped || sliderDetails.imageUrl,
                      )}
                      alt={sliderDetails.title || "Slider"}
                      className="w-full h-64 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="450" fill="none"%3E%3Crect width="800" height="450" fill="%23374151"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23ffffff" font-size="20"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <div className="absolute top-2 right-2">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          sliderDetails.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {sliderDetails.isActive ? (
                          <>
                            <EyeIcon className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <EyeSlashIcon className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Title
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {sliderDetails.title || "-"}
                    </p>
                  </div>

                  {/* Button Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Button Text
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {sliderDetails.buttonText || "-"}
                    </p>
                  </div>

                  {/* Link Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Link Type
                    </label>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        sliderDetails.linkType === "internal"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                          : "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                      }`}
                    >
                      {sliderDetails.linkType}
                    </span>
                  </div>

                  {/* Display Order */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Order
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {sliderDetails.displayOrder}
                    </p>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Language
                    </label>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {sliderDetails.language?.toUpperCase() || "N/A"}
                    </span>
                  </div>

                  {/* Link Value */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Link Value
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white break-all">
                      {sliderDetails.linkValue}
                    </p>
                  </div>

                  {/* Description */}
                  {sliderDetails.description && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {sliderDetails.description}
                      </p>
                    </div>
                  )}

                  {/* Countries */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Active Countries ({sliderDetails.countries.length})
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {sliderDetails.countries.map((country) => (
                        <span
                          key={country}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        >
                          {country}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Created At
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(sliderDetails.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Last Updated
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {new Date(sliderDetails.updatedAt).toLocaleString()}
                    </p>
                  </div>

                  {/* Image URLs */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Image URLs
                    </label>
                    <div className="space-y-1">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Original:</span>{" "}
                        {sliderDetails.imageUrl}
                      </p>
                      {sliderDetails.imageUrlCropped && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Cropped:</span>{" "}
                          {sliderDetails.imageUrlCropped}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setDetailsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDetailsModalOpen(false);
                    handleEdit(sliderDetails);
                  }}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  <PencilIcon className="h-4 w-4 inline mr-2" />
                  Edit Slider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={handleDeleteCancel}
            />

            {/* Modal */}
            <div className="inline-block w-full max-w-md px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 dark:bg-red-900 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <TrashIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Delete Slide
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete "
                      {sliderToDelete?.title ||
                        sliderToDelete?.buttonText ||
                        "this slide"}
                      "? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteSlider.isPending}
                  className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteSlider.isPending ? "Deleting..." : "Delete"}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  disabled={deleteSlider.isPending}
                  className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sliders;
